import { useState } from 'react';
import { useMediaLibrary } from 'routes/settings/publisher/generalOrderSettings/ClipArtSettings/ClipArtLibrary/clipArtHelpers';
import { NewspaperOrdersFormData } from 'routes/ads/place/PlacementFlowStepSelector';
import { Layout } from 'lib/types/layout';
import { logAndCaptureCriticalError } from 'utils';
import { ColumnService } from 'lib/services/directory';
import useAsyncEffect from 'lib/frontend/hooks/useAsyncEffect';
import { getOrThrow } from 'lib/utils/refs';
import { getAdWidth } from 'lib/orders/layouts';
import { OrderImage } from 'lib/orders/images';
import { Ad } from 'lib/types/ad';
import { Product } from 'lib/enums';
import { minBy } from 'lodash';
import OrderImageEditor from '../Images/OrderImageEditor';
import { AdEditorData } from '../FormattedEditorWrapper';
import TitleEditor from './TitleEditor';
import ContentEditor, { MadlibEditorProps } from './ContentEditor';

type FormattedEditorProps<T extends Ad> = {
  adData: Partial<T>;
  onChange: (data: AdEditorData) => void;
  layout: Layout;
  newspaperOrdersFormData: NewspaperOrdersFormData;
  disableEditing: boolean;
  product: Product;
  madlibProps: MadlibEditorProps;
  forceFreeEdit: boolean;
  advertiserLogo?: string;
};

function FormattedEditor<T extends Ad>({
  adData,
  onChange,
  layout,
  newspaperOrdersFormData,
  disableEditing,
  product,
  madlibProps,
  forceFreeEdit,
  advertiserLogo
}: FormattedEditorProps<T>) {
  const [firstNewspaperOrder] = newspaperOrdersFormData;
  // Make a local copy of orderImages that can include nulls for empty slots and avoid mutating props
  const [localOrderImages, setLocalOrderImages] = useState<
    (OrderImage | null)[]
  >(adData.orderImages || []);

  function onUpdateImages(updatedImages: (OrderImage | null)[]) {
    setLocalOrderImages(updatedImages);
    onChange({ orderImages: updatedImages.filter(Boolean) as OrderImage[] });
  }

  const { value: adWidth, isLoading: isAdWidthLoading } = useAsyncEffect({
    fetchData: async () => {
      if (!newspaperOrdersFormData.length) return;
      const filingType = await getOrThrow(firstNewspaperOrder.filingType);
      const template = await getOrThrow(filingType.data().template);
      const templateStyles = template.data().styles;
      if (!templateStyles) {
        logAndCaptureCriticalError(
          ColumnService.ORDER_PLACEMENT,
          new Error('No styles found for ad template'),
          'No styles found for ad template',
          {
            publisher: firstNewspaperOrder.newspaper?.id || '',
            template: template.id
          }
        );
        return;
      }

      const { response: adWidth, error: adWidthError } = getAdWidth({
        layout,
        templateStyles
      });

      if (adWidthError) {
        logAndCaptureCriticalError(
          ColumnService.ORDER_PLACEMENT,
          adWidthError,
          'Error getting ad width'
        );
      }
      return adWidth;
    },
    dependencies: [
      layout.id,
      newspaperOrdersFormData.map(o => o.newspaper?.id).join(',')
    ]
  });

  const { value: maximumWordCountByFilingType, isLoading: loadingFilingType } =
    useAsyncEffect({
      fetchData: async () => {
        const filingTypeRefsPromise = newspaperOrdersFormData.map(orderForm =>
          getOrThrow(orderForm.filingType)
        );

        const filingTypes = await Promise.all(filingTypeRefsPromise);
        const filingTypeSnapshots = filingTypes.map(ftSnap => ftSnap.data());
        const applicableRange = minBy(
          filingTypeSnapshots,
          ft => ft.maxWordCount
        );
        if (!applicableRange) {
          return null;
        }
        return applicableRange.maxWordCount;
      },
      dependencies: [
        newspaperOrdersFormData.map(o => o.filingType?.id).join(',')
      ],
      errorConfig: {
        service: ColumnService.ORDER_PLACEMENT,
        message: 'Failed to load filing type ad data'
      }
    });

  /**
   * NOTE: We are not handling an error or loading state here because it will fall back to only upload if there is an error.
   * Errors are still logged within the hook for observability.
   */
  const { value: mediaList } = useMediaLibrary({
    organizationId: firstNewspaperOrder.newspaper?.id ?? '',
    folder: product
  });

  if (isAdWidthLoading) return null;

  return (
    <div
      className={
        'bg-white origin-top lg:origin-top-left lg:mt-4 align-baseline transform border'
      }
      style={{
        width: `${adWidth}px`,
        minWidth: `175px`, // If the editor is too small based on the template size, it is difficult to use
        borderColor:
          firstNewspaperOrder.colorOptions?.borderColor || 'transparent'
      }}
    >
      <div className="border border-dashed">
        <OrderImageEditor
          position="top"
          orderImages={localOrderImages}
          mediaList={mediaList}
          onUpdateImages={onUpdateImages}
          layout={layout}
          disableEditing={disableEditing}
          advertiserLogo={advertiserLogo}
        />
        {!layout.titleConfig?.hidden && (
          <h1
            className="overflow-auto border-dashed border-b py-4 px-1"
            style={{
              backgroundColor:
                newspaperOrdersFormData[0].colorOptions?.backgroundColor ||
                'transparent'
            }}
          >
            <TitleEditor
              adData={adData}
              disableEditing={disableEditing}
              product={product}
              onChange={onChange}
              layout={layout}
            />
          </h1>
        )}
        {!layout.bodyConfig?.hidden && (
          <>
            <div
              className="px-1"
              style={{
                backgroundColor:
                  newspaperOrdersFormData[0].colorOptions?.backgroundColor ||
                  'transparent'
              }}
            >
              <ContentEditor
                onChange={onChange}
                adData={adData}
                disableEditing={disableEditing || loadingFilingType}
                product={product}
                madlibProps={madlibProps}
                forceFreeEdit={forceFreeEdit}
                maxWords={maximumWordCountByFilingType}
                layout={layout}
              />
            </div>
          </>
        )}
        <OrderImageEditor
          position="bottom"
          orderImages={localOrderImages}
          mediaList={mediaList}
          onUpdateImages={onUpdateImages}
          layout={layout}
          disableEditing={disableEditing}
          advertiserLogo={advertiserLogo}
        />
      </div>
    </div>
  );
}

export default FormattedEditor;
